import { useEffect, useState } from 'react'
import {
  Modal,
  Stack,
  Select,
  Textarea,
  TextInput,
  Group,
  Button,
  Text,
  Alert,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useExecutarOcorrencia } from '../../hooks/useOcorrencias'
import { STATUS_OCORRENCIA, labelDe } from '../planos_modelo/constants'
import { extractApiError } from '../../lib/extractApiError'
import type {
  ExecutarOcorrenciaPayload,
  Ocorrencia,
  StatusOcorrencia,
} from '../../types/pmocPlanos'

interface Props {
  ocorrencia: Ocorrencia | null
  onClose: () => void
}

const STATUS_EDITAVEIS: Array<{ value: StatusOcorrencia; label: string }> = [
  { value: 'em_execucao', label: 'Em execução' },
  { value: 'cumprida', label: 'Cumprida' },
  { value: 'cumprida_parcial', label: 'Cumprida parcial' },
  { value: 'nao_cumprida', label: 'Não cumprida' },
  { value: 'cancelada', label: 'Cancelada' },
]

export default function RegistrarExecucaoModal({ ocorrencia, onClose }: Props) {
  const executar = useExecutarOcorrencia()
  const [form, setForm] = useState<ExecutarOcorrenciaPayload>({})

  useEffect(() => {
    if (!ocorrencia) return
    setForm({
      status: ocorrencia.status,
      observacao: ocorrencia.observacao,
      motivo_nao_cumprida: ocorrencia.motivo_nao_cumprida,
    })
  }, [ocorrencia?.id])

  if (!ocorrencia) return null
  const ocorrenciaAtiva = ocorrencia

  async function handleSalvar() {
    if (form.status === 'nao_cumprida' && !(form.motivo_nao_cumprida ?? '').trim()) {
      notifications.show({
        color: 'red',
        message: 'Motivo é obrigatório quando status = "Não cumprida".',
      })
      return
    }
    if (form.status === 'cumprida_parcial' && !(form.observacao ?? '').trim()) {
      notifications.show({
        color: 'red',
        message:
          'Observação é obrigatória quando status = "Cumprida parcial".',
      })
      return
    }
    try {
      await executar.mutateAsync({ id: ocorrenciaAtiva.id, data: form })
      notifications.show({ color: 'green', message: 'Execução registrada.' })
      onClose()
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Falha',
        message: extractApiError(err, 'Erro ao registrar execução.'),
      })
    }
  }

  return (
    <Modal
      opened={!!ocorrencia}
      onClose={onClose}
      title={`Registrar execução — ${ocorrencia.atividade_nome}`}
      size="lg"
      centered
    >
      <Stack gap="md">
        <Alert color="blue" variant="light">
          <Text size="sm">
            <strong>Atividade:</strong> {ocorrencia.atividade_nome}
            <br />
            <strong>Prevista para:</strong> {ocorrencia.data_prevista}
            {ocorrencia.data_limite && ` · Limite: ${ocorrencia.data_limite}`}
            {ocorrencia.vencida && (
              <Text component="span" c="red" fw={600}>
                {' '}
                · Vencida
              </Text>
            )}
          </Text>
        </Alert>

        <Select
          label="Status"
          data={STATUS_EDITAVEIS}
          value={form.status ?? 'em_execucao'}
          onChange={(v) =>
            v && setForm({ ...form, status: v as StatusOcorrencia })
          }
          data-testid="execucao-status"
        />

        <Group grow>
          <TextInput
            label="Iniciado em"
            type="datetime-local"
            value={form.iniciado_em ?? ''}
            onChange={(e) =>
              setForm({ ...form, iniciado_em: e.currentTarget.value || null })
            }
          />
          <TextInput
            label="Executado em"
            type="datetime-local"
            value={form.executado_em ?? ''}
            onChange={(e) =>
              setForm({ ...form, executado_em: e.currentTarget.value || null })
            }
          />
        </Group>

        <Textarea
          label={
            form.status === 'cumprida_parcial'
              ? 'Observação (obrigatória) *'
              : 'Observação'
          }
          autosize
          minRows={2}
          value={form.observacao ?? ''}
          onChange={(e) =>
            setForm({ ...form, observacao: e.currentTarget.value })
          }
        />

        {form.status === 'nao_cumprida' && (
          <Textarea
            label="Motivo da não cumprimento *"
            required
            autosize
            minRows={2}
            value={form.motivo_nao_cumprida ?? ''}
            onChange={(e) =>
              setForm({ ...form, motivo_nao_cumprida: e.currentTarget.value })
            }
            data-testid="execucao-motivo"
          />
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={handleSalvar}
            loading={executar.isPending}
            data-testid="execucao-submit"
          >
            Salvar (novo status: {labelDe(form.status ?? '', STATUS_OCORRENCIA)})
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
